import { useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Database,
  LoaderCircle,
  Search,
  Sparkles,
} from 'lucide-react';
import { OperatorErrorText } from '../renderer/TechnicalDetails';
import type { PaigeDestinationsBridge } from './paige.global';
import type { PaigeCcCatalogEntry } from './product-destination-types';
import { CATALOG_LIMIT, reasonOf, useCatalogPicker } from './use-content-central';
import './content-central-sheet.css';

export interface ContentCentralCatalogPickerProps {
  bridge: PaigeDestinationsBridge;
  destinationId: string;
  /** The catalog chosen now (its id), and its name once read; null when none is chosen yet. */
  current: { id: string; name: string } | null;
  onChoose: (catalog: { id: string; name: string }) => void;
  /** "Create a new catalog from this job". */
  onCreate: () => void;
}

type Picker = ReturnType<typeof useCatalogPicker>;

/**
 * The destination's catalog, as CapturePoint's Catalog step (ContentCentralSetup.tsx): the catalog in use with
 * Change, or the list of what the stored key can see, searched by name, a page at a time with Show more. A
 * catalog the key may only read is marked View only and cannot be chosen; "Create a new catalog from this job"
 * is always last. An empty list says what that means: Content Central lists only what the key may use.
 */
export function ContentCentralCatalogPicker(props: ContentCentralCatalogPickerProps) {
  const { current, onChoose } = props;
  const [changing, setChanging] = useState(!current);
  if (current && !changing)
    return (
      <fieldset className="paige-cc-catalog">
        <legend>Catalog</legend>
        <span className="paige-cc-row">
          <Database size={16} aria-hidden="true" />
          <strong>{current.name || 'Reading the catalog…'}</strong>
          <button type="button" className="quiet-button small" onClick={() => setChanging(true)}>
            Change
          </button>
        </span>
      </fieldset>
    );
  return (
    <CatalogList
      {...props}
      onChoose={(catalog) => (setChanging(false), onChoose(catalog))}
      onKeep={current ? () => setChanging(false) : undefined}
    />
  );
}

function CatalogList(
  props: ContentCentralCatalogPickerProps & { onKeep?: (() => void) | undefined },
) {
  const { bridge, destinationId, current, onChoose, onCreate, onKeep } = props;
  const picker = useCatalogPicker(bridge, destinationId);
  return (
    <fieldset className="paige-cc-catalog">
      <legend>Catalog</legend>
      {current && onKeep && <KeepCurrent name={current.name} onKeep={onKeep} />}
      <label className="paige-cc-search">
        <Search size={15} aria-hidden="true" />
        <input
          value={picker.query}
          onChange={(event) => picker.setQuery(event.target.value)}
          placeholder="Find a catalog"
          aria-label="Find a catalog"
        />
      </label>
      {picker.state === 'failed' ? (
        <LoadFailed error={picker.error} onRetry={picker.reload} />
      ) : (
        <CatalogItems picker={picker} currentId={current?.id} onChoose={onChoose} />
      )}
      <ShowMore picker={picker} />
      <CreateCard
        canCreate={picker.canCreate}
        disabled={picker.state !== 'loaded'}
        onCreate={onCreate}
      />
    </fieldset>
  );
}

function KeepCurrent({ name, onKeep }: { name: string; onKeep: () => void }) {
  return (
    <span className="paige-cc-row">
      <Database size={16} aria-hidden="true" />
      <span className="paige-cc-text">
        <strong>{name}</strong>
        <small>This destination&apos;s catalog now · pick another below</small>
      </span>
      <button type="button" className="secondary-button small" onClick={onKeep}>
        Keep this catalog
      </button>
    </span>
  );
}

function CatalogItems(props: {
  picker: Picker;
  currentId: string | undefined;
  onChoose: ContentCentralCatalogPickerProps['onChoose'];
}) {
  const { picker, currentId, onChoose } = props;
  return (
    <ul
      className="paige-cc-list"
      aria-label="Content Central catalogs"
      aria-busy={picker.state === 'loading'}
    >
      {picker.state === 'loading' && picker.items.length === 0 && (
        <li className="paige-cc-state" role="status">
          <LoaderCircle className="spin" size={14} aria-hidden="true" /> Finding catalogs…
        </li>
      )}
      {picker.items.map((catalog) => (
        <li key={catalog.id}>
          {catalog.canAddDocuments ? (
            <CatalogChoice
              catalog={catalog}
              current={currentId === catalog.id}
              onChoose={onChoose}
            />
          ) : (
            <ViewOnly catalog={catalog} />
          )}
        </li>
      ))}
      <EmptyLine picker={picker} />
    </ul>
  );
}

function ShowMore({ picker }: { picker: Picker }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const showMore = () => {
    setBusy(true);
    setError('');
    picker
      .more()
      .catch((failure: unknown) => setError(reasonOf(failure, 'More catalogs could not be loaded')))
      .finally(() => setBusy(false));
  };
  if (picker.items.length >= CATALOG_LIMIT)
    return (
      <small>
        Search by name to find catalogs beyond these {CATALOG_LIMIT.toLocaleString()} results.
      </small>
    );
  return (
    <>
      {error && <OperatorErrorText message={error} className="paige-destination-error" />}
      {picker.cursor && (
        <span className="paige-cc-more">
          <small>{picker.items.length.toLocaleString()} shown</small>
          <button
            type="button"
            className="secondary-button small"
            disabled={busy}
            onClick={showMore}
          >
            <ChevronDown size={13} aria-hidden="true" />
            {busy ? 'Loading…' : 'Show more'}
          </button>
        </span>
      )}
    </>
  );
}

function CatalogText({ catalog }: { catalog: PaigeCcCatalogEntry }) {
  const count = catalog.documentTypeCount;
  return (
    <>
      <Database size={15} aria-hidden="true" />
      <span className="paige-cc-text">
        <strong>{catalog.name}</strong>
        <small>{`${count} document ${count === 1 ? 'type' : 'types'}`}</small>
      </span>
    </>
  );
}

function ViewOnly({ catalog }: { catalog: PaigeCcCatalogEntry }) {
  return (
    <div
      className="paige-cc-choice is-view-only"
      aria-disabled="true"
      title="Can't add documents here"
    >
      <CatalogText catalog={catalog} />
      <span className="paige-cc-pill">View only</span>
    </div>
  );
}

function CatalogChoice(props: {
  catalog: PaigeCcCatalogEntry;
  current: boolean;
  onChoose: ContentCentralCatalogPickerProps['onChoose'];
}) {
  const { catalog, current, onChoose } = props;
  return (
    <button
      type="button"
      className={`paige-cc-choice${current ? ' is-current' : ''}`}
      onClick={() => onChoose({ id: catalog.id, name: catalog.name })}
    >
      <CatalogText catalog={catalog} />
      {current ? (
        <span className="paige-cc-pill is-ready">
          <Check size={12} aria-hidden="true" />
          Current
        </span>
      ) : (
        <ChevronRight size={16} aria-hidden="true" />
      )}
    </button>
  );
}

function EmptyLine({ picker }: { picker: Picker }) {
  if (picker.state !== 'loaded' || picker.items.length > 0) return null;
  return picker.query.trim() ? (
    <li className="paige-cc-state" role="status">
      No catalog matches “{picker.query.trim()}”.{' '}
      <button type="button" className="quiet-button small" onClick={() => picker.setQuery('')}>
        Clear search
      </button>
    </li>
  ) : (
    <li className="paige-cc-state">
      This integration key can&apos;t see any catalogs yet. Give it access in Content Central, or
      create one from this job.
    </li>
  );
}

function LoadFailed({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="paige-cc-error" role="alert">
      <span>
        <strong>Catalogs could not be loaded</strong>
        <OperatorErrorText message={error} />
      </span>
      <button type="button" className="secondary-button small" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function CreateCard(props: { canCreate: boolean | null; disabled: boolean; onCreate: () => void }) {
  const { canCreate, disabled, onCreate } = props;
  return (
    <button type="button" className="paige-cc-create" disabled={disabled} onClick={onCreate}>
      <Sparkles size={16} aria-hidden="true" />
      <span className="paige-cc-text">
        <strong>Create a new catalog from this job</strong>
        <small>
          {canCreate === false
            ? 'This key may add documents but not build catalogs; Content Central has to allow it first'
            : 'Content Central builds the types, fields and filing to match'}
        </small>
      </span>
      <ChevronRight size={16} aria-hidden="true" />
    </button>
  );
}
